/**
 * Pure helper: turn the wizard's single parking-spot count into an
 * ordered list of payloads suitable for `replace_parking_lot_spots`.
 *
 * Wizard contract: the user enters one integer (estimated number of
 * parking spots). OptiBay creates a default `parking_lot_layouts` row
 * (transparent to the user — see `commitWizard.ts`) and writes N spots
 * into it. The user can drag/edit positions later in the existing
 * parking editor; the wizard does not surface "layout" as a concept.
 *
 * Constraints from `replace_parking_lot_spots` (migration 20260120031425):
 *   - Each spot must carry a non-null `geom` JSONB (RPC raises if null).
 *   - `label` must be non-empty and unique within the layout.
 *   - `spot_type` defaults to 'STANDARD' if omitted.
 *
 * `geom` shape: a minimal valid GeoJSON polygon. Real coordinates come
 * later when the operator calibrates against an uploaded lot image.
 * For wizard MVP we emit tiny deterministic placeholders so the editor
 * can render and reposition.
 */

export interface GeneratedParkingSpotPayload {
  label: string;
  spot_type: 'STANDARD';
  geom: {
    type: 'Polygon';
    coordinates: [Array<[number, number]>];
  };
  is_active: true;
  /**
   * Pixel geometry Birdseye uses to render each spot.
   * `replace_parking_lot_spots` (migration 20260704120000) persists
   * position_x / position_y / width / height from this same payload,
   * atomically with the spot rows — there is no follow-up UPDATE.
   *
   * Layout doctrine: the parking grid sits BELOW the bay area so the
   * two never overlap. Onboarding bay columns are placed cumulatively by
   * each tech's widest final bay, rows pitched BAY_HEIGHT + BAY_SPACING
   * (see `generateBayLayout.ts`); onboarding passes startY from
   * `onboardingParkingStartY()`, i.e. below the deepest ACTUAL generated
   * bay, so no fixed bay-row count is assumed.
   */
  position_x: number;
  position_y: number;
  width: number;
  height: number;
}

const SPOTS_PER_ROW = 10;
const SPOT_PLACEHOLDER_DEGREES = 0.0001; // tiny offset; just keeps geom non-degenerate

// Spot size and gap come from the canonical parking constants shared
// with the Facility Editor, so there is one parking default-size system.
// Placement is the caller's: startX/startY may be supplied, and
// onboarding supplies a dynamic startY derived from the actual generated
// bay geometry (see `onboardingParkingStartY`).
import {
  PARKING_WIDTH,
  PARKING_HEIGHT,
  PARKING_GAP,
} from '@/components/facility/constants';

// Pixel-grid constants (all GRID_SIZE multiples).
//   - Placement: the parking block starts at (startX, startY). Onboarding
//     passes startY = onboardingParkingStartY(generated bays): the deepest
//     ACTUAL generated bay bottom + ONBOARDING_BAY_PARKING_GAP, so parking
//     clears the bay area for any number of bays per tech (no fixed
//     row-count assumption). With no bays / no start given it begins at
//     the top margin.
//   - Each spot is the canonical PARKING_WIDTH×PARKING_HEIGHT
//     (180×360 ≈ 9×18 ft, vertical) with a PARKING_GAP (20px) gap on
//     both axes, i.e. 200×380px pitch. With SPOTS_PER_ROW=10 that's a
//     2000px-wide parking block.
const ONBOARDING_TOP_MARGIN_PX = 20;
const PARKING_OFFSET_X_PX = 20;
/** Vertical gap between the deepest onboarding bay and the parking block. */
export const ONBOARDING_BAY_PARKING_GAP = 100;
const PARKING_SPOT_WIDTH_PX = PARKING_WIDTH;
const PARKING_SPOT_HEIGHT_PX = PARKING_HEIGHT;
const PARKING_SPOT_PITCH_X_PX = PARKING_WIDTH + PARKING_GAP;
const PARKING_SPOT_PITCH_Y_PX = PARKING_HEIGHT + PARKING_GAP;

/** Pad an integer to a 3-digit zero-prefixed string (e.g. 1 → "001"). */
function pad3(n: number): string {
  return n.toString().padStart(3, '0');
}

function makePlaceholderPolygon(col: number, row: number) {
  // Each spot gets a tiny 1-unit square in degree space.
  const x = col * SPOT_PLACEHOLDER_DEGREES;
  const y = row * SPOT_PLACEHOLDER_DEGREES;
  const w = SPOT_PLACEHOLDER_DEGREES;
  return {
    type: 'Polygon' as const,
    coordinates: [
      [
        [x, y] as [number, number],
        [x + w, y] as [number, number],
        [x + w, y + w] as [number, number],
        [x, y + w] as [number, number],
        [x, y] as [number, number],
      ],
    ] as [Array<[number, number]>],
  };
}

/**
 * Where onboarding parking should start (y), from the ACTUAL generated bay
 * geometry: deepest bay bottom (position_y + height) + the bay→parking gap.
 * No bays → the top margin. Pure; takes the in-memory generated bays.
 */
export function onboardingParkingStartY(
  bays: ReadonlyArray<{ position_y: number; height: number }>,
): number {
  if (bays.length === 0) return ONBOARDING_TOP_MARGIN_PX;
  const deepestBayBottom = Math.max(...bays.map(b => b.position_y + b.height));
  return deepestBayBottom + ONBOARDING_BAY_PARKING_GAP;
}

export interface GenerateParkingLayoutOptions {
  /** Left edge of the parking block (default 20). */
  startX?: number;
  /** Top edge of the parking block (default: top margin, 20). */
  startY?: number;
}

export function generateParkingLayout(
  count: number,
  options: GenerateParkingLayoutOptions = {},
): GeneratedParkingSpotPayload[] {
  if (!Number.isFinite(count) || count <= 0) return [];
  const startX = options.startX ?? PARKING_OFFSET_X_PX;
  const startY = options.startY ?? ONBOARDING_TOP_MARGIN_PX;
  const safeCount = Math.floor(count);
  const out: GeneratedParkingSpotPayload[] = [];
  for (let i = 0; i < safeCount; i++) {
    const col = i % SPOTS_PER_ROW;
    const row = Math.floor(i / SPOTS_PER_ROW);
    out.push({
      label: pad3(i + 1),
      spot_type: 'STANDARD',
      geom: makePlaceholderPolygon(col, row),
      is_active: true,
      position_x: startX + col * PARKING_SPOT_PITCH_X_PX,
      position_y: startY + row * PARKING_SPOT_PITCH_Y_PX,
      width: PARKING_SPOT_WIDTH_PX,
      height: PARKING_SPOT_HEIGHT_PX,
    });
  }
  return out;
}
